import React, { useState, useEffect } from 'react';
import { useFinancial } from "../../../context/FinancialContext";
import { useEmpresaSelector } from "../../../context/EmpresaSelectorContext";
import { useAuth } from "../../../context/AuthContext";
import { apiRequest } from "../../../utils/api";
import "../../../styles/patrimonio.css";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Patrimonio = () => {
  const { 
    patrimonioData: contextPatrimonioData, 
    setPatrimonioData, 
    activos, 
    pasivos,
    savePatrimonio 
  } = useFinancial();
  
  const { empresaSeleccionada, esConexAdmin, cambiarEmpresa, empresas } = useEmpresaSelector();
  const { usuario: authUsuario } = useAuth();
  
  // Estado para controlar el guardado
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  // Definir rango fijo de años (2024 a 2050)
  const availableYears = Array.from({ length: 2050 - 2024 + 1 }, (_, i) => 2024 + i);

  // Estructura inicial de datos patrimoniales por año (fallback si no existe en context)
  const initialYearData = {
    activos: {
      corrientes: [
        { id: 1, concepto: 'Disponibilidad', valor: 31501.25, formula: 'manual', patrimonioId: null },
        { id: 2, concepto: 'Créditos', valor: 58753.30, formula: 'manual', patrimonioId: null },
        { id: 3, concepto: 'Bienes de Cambio', valor: 144538.30, formula: 'manual', patrimonioId: null }
      ],
      noCorrientes: [
        { id: 4, concepto: 'Bienes de Uso', valor: 427508.14, formula: 'manual', patrimonioId: null },
        { id: 5, concepto: 'Plazo Fijo', valor: 320000.00, formula: 'manual', patrimonioId: null },
        { id: 6, concepto: 'Terrenos', valor: 550000.00, formula: 'manual', patrimonioId: null }
      ]
    },
    pasivos: {
      corrientes: [
        { id: 7, concepto: 'Proveedores', valor: 515.78, formula: 'manual', patrimonioId: null },
        { id: 8, concepto: 'Acreedores', valor: 82963.20, formula: 'manual', patrimonioId: null },
        { id: 9, concepto: 'Deuda de Corto Plazo', valor: 0.00, formula: 'manual', patrimonioId: null }
      ],
      noCorrientes: [
        { id: 10, concepto: 'Deudas con entidades de crédito', valor: 121000.00, formula: 'manual', patrimonioId: null },
        { id: 11, concepto: 'Otras deudas a largo plazo', valor: 850000.00, formula: 'manual', patrimonioId: null },
        { id: 12, concepto: 'Impuestos diferidos', valor: 5400.00, formula: 'manual', patrimonioId: null }
      ]
    }
  };

  // Estado para datos del balance patrimonio
  const [balanceData, setBalanceData] = useState(initialYearData);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Ensure we have a valid empresa ID
  const empresaId = empresaSeleccionada?.id || 'default';

  // Función para cargar datos del balance desde el backend
  const loadBalanceData = async () => {
    try {
      setLoading(true);
      let endpoint = '/patrimonios';
      if (esConexAdmin && empresaSeleccionada?.id && empresaSeleccionada.id !== 'all') {
        endpoint += `?empresaId=${empresaSeleccionada.id}`;
      }

      const response = await apiRequest(endpoint);

      if (response.ok) {
        const data = await response.json();
        // Map backend data to frontend structure
        if (Array.isArray(data) && data.length > 0) {
          // Sort data by fecha descending to get most recent entries
          const sortedData = data.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

          // Group data by logical sections (we'll determine sections by position in the sorted data)
          const mappedData = {
            activos: {
              corrientes: [],
              noCorrientes: []
            },
            pasivos: {
              corrientes: [],
              noCorrientes: []
            }
          };

          // Categorize items based on their tipo field from database
          sortedData.forEach((patrimonio, index) => {
            const item = {
              id: index + 1, // Use index + 1 as local ID
              concepto: patrimonio.concepto || 'Concepto',
              valor: patrimonio.valor || 0,
              formula: 'manual',
              patrimonioId: patrimonio.id,
              tipo: patrimonio.tipo || 'activo_corriente' // Use stored tipo or default to activo_corriente
            };

            // Categorize based on tipo field
            switch (item.tipo) {
              case 'activo_corriente':
                mappedData.activos.corrientes.push(item);
                break;
              case 'activo_no_corriente':
                mappedData.activos.noCorrientes.push(item);
                break;
              case 'pasivo_corriente':
                mappedData.pasivos.corrientes.push(item);
                break;
              case 'pasivo_no_corriente':
                mappedData.pasivos.noCorrientes.push(item);
                break;
              default:
                // For items without tipo field (legacy data), put in activos corrientes
                mappedData.activos.corrientes.push(item);
                break;
            }
          });

          // No need to ensure minimum items - let sections be empty if no data
          setBalanceData(mappedData);
        } else {
          // No data from backend, show empty structure for adding new items
          const emptyData = {
            activos: {
              corrientes: [],
              noCorrientes: []
            },
            pasivos: {
              corrientes: [],
              noCorrientes: []
            }
          };
          setBalanceData(emptyData);
        }
      } else {
        setBalanceData(initialYearData);
      }
    } catch (error) {
      console.error('Error en loadBalanceData:', error);
      setBalanceData(initialYearData);
    } finally {
      setLoading(false);
    }
  };

  // Función para guardar datos del balance al backend
  const saveBalanceData = async (dataToSave = balanceData) => {
    try {
      setSaving(true);

      // Check if we have valid empresa selected
      if (!empresaSeleccionada?.id || empresaSeleccionada.id === 'all') {
        toast.error('Por favor selecciona una empresa específica antes de guardar.');
        return false;
      }

      // Check if we have valid user
      if (!authUsuario?.id) {
        toast.error('Error: Usuario no identificado. Por favor vuelve a iniciar sesión.');
        return false;
      }
      // Save each item in the balance as a separate patrimonio record
      // Don't rely on item.tipo field, determine tipo based on position in structure
      const allItems = [
        ...dataToSave.activos.corrientes.map(item => ({ ...item, tipo: 'activo_corriente', seccion: 'activos' })),
        ...dataToSave.activos.noCorrientes.map(item => ({ ...item, tipo: 'activo_no_corriente', seccion: 'activos' })),
        ...dataToSave.pasivos.corrientes.map(item => ({ ...item, tipo: 'pasivo_corriente', seccion: 'pasivos' })),
        ...dataToSave.pasivos.noCorrientes.map(item => ({ ...item, tipo: 'pasivo_no_corriente', seccion: 'pasivos' })),
      ];
      const results = [];
      for (let i = 0; i < allItems.length; i++) {
        const item = allItems[i];
        const empresaIdValue = empresaSeleccionada.id;
        const patrimonioData = {
          concepto: item.concepto || 'Nuevo Concepto',
          valor: parseFloat(item.valor) || 0,
          tipo: item.tipo, // Now enabled since database migration was applied
          usuarioId: authUsuario.id, // Required by schema
          empresaId: empresaIdValue,
          fecha: new Date().toISOString()
        };
        // Use existing patrimonioId if available, otherwise create new
        const method = item.patrimonioId ? 'PUT' : 'POST';
        const url = item.patrimonioId ? `/patrimonios/${item.patrimonioId}` : '/patrimonios';

        try {
          const response = await apiRequest(url, {
            method: method,
            body: JSON.stringify(patrimonioData),
          });
            if (!response.ok) {
              const errorText = await response.text();
              console.error(`Error saving item ${item.id}:`, response.status, errorText);
              results.push(false);
            } else {
              const successData = await response.json();
          // Update the patrimonioId in the frontend data if it was newly created
          if (!item.patrimonioId && successData.id) {
            // Find and update the item in balanceData based on tipo field
            const updateItemInData = (section, type, localId, newPatrimonioId) => {
              setBalanceData(prev => ({
                ...prev,
                [section]: {
                  ...prev[section],
                  [type]: prev[section][type].map(it =>
                    it.id === localId ? { ...it, patrimonioId: newPatrimonioId } : it
                  )
                }
              }));
            };

            // Determine which section and type this item belongs to based on tipo field
            if (item.tipo === 'activo_corriente') {
              updateItemInData('activos', 'corrientes', item.id, successData.id);
            } else if (item.tipo === 'activo_no_corriente') {
              updateItemInData('activos', 'noCorrientes', item.id, successData.id);
            } else if (item.tipo === 'pasivo_corriente') {
              updateItemInData('pasivos', 'corrientes', item.id, successData.id);
            } else if (item.tipo === 'pasivo_no_corriente') {
              updateItemInData('pasivos', 'noCorrientes', item.id, successData.id);
            }
          }
        }
        results.push(response.ok);
        } catch (error) {
          console.error(`Error saving item ${item.id}:`, error);
          results.push(false);
        }
      }
      const successCount = results.filter(Boolean).length;

      if (successCount > 0) {
        return true;
      } else {
        console.error('Error: No se pudo guardar ningún item');
        return false;
      }
    } catch (error) {
      console.error('Error en saveBalanceData:', error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    const loadData = async () => {
      await loadBalanceData();
    };
    loadData();
  }, [empresaSeleccionada?.id, esConexAdmin]);

  const [selectedYear, setSelectedYear] = useState(2024);
  const [companyName, setCompanyName] = useState('conex');
  const [editingItem, setEditingItem] = useState(null);

  // Usar balanceData directamente
  const currentData = balanceData;

  // --- SUMAS GLOBALES ---
  // Activos globales del año seleccionado
  const activosGlobales = (activos || []).filter(a => {
    // Si tienes un campo de año en activos, filtra aquí. Si no, usa todos.
    return true;
  });
  const totalActivosGlobal = activosGlobales.reduce((sum, a) => sum + (parseFloat(a.valorActivo) || 0), 0);

  // Pasivos globales del año seleccionado
  const pasivosGlobales = (pasivos || []).filter(p => {
    // Si tienes un campo de año en pasivos, filtra aquí. Si no, usa todos.
    return true;
  });
  const totalPasivosGlobal = pasivosGlobales.reduce((sum, p) => sum + (parseFloat(p.montoActual) || 0), 0);

  // --- SUMAS MANUALES (locales) ---
  const calcularTotalActivoCorriente = () => {
    return (currentData.activos?.corrientes || []).reduce((sum, item) => sum + (parseFloat(item?.valor) || 0), 0);
  };
  const calcularTotalActivoNoCorriente = () => {
    return (currentData.activos?.noCorrientes || []).reduce((sum, item) => sum + (parseFloat(item?.valor) || 0), 0);
  };
  const calcularTotalActivoManual = () => calcularTotalActivoCorriente() + calcularTotalActivoNoCorriente();

  const calcularTotalPasivoCorriente = () => {
    return (currentData.pasivos?.corrientes || []).reduce((sum, item) => sum + (parseFloat(item?.valor) || 0), 0);
  };
  const calcularTotalPasivoNoCorriente = () => {
    return (currentData.pasivos?.noCorrientes || []).reduce((sum, item) => sum + (parseFloat(item?.valor) || 0), 0);
  };
  const calcularTotalPasivoManual = () => calcularTotalPasivoCorriente() + calcularTotalPasivoNoCorriente();

  // --- SUMA FINAL ---
  // For Patrimonio page, only use manual entries, not global context data
  const calcularTotalActivo = () => calcularTotalActivoManual();
  const calcularTotalPasivo = () => calcularTotalPasivoManual();
  const calcularPatrimonioNeto = () => calcularTotalActivo() - calcularTotalPasivo();

  // Formatear moneda
  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(valor ?? 0);
  };

  // Determinar si el texto o valor es "largo" para ajustar el tamaño de fuente
  const getTextClass = (text) => {
    if (typeof text === 'string' && text.length > 15) return 'small-text';
    if (typeof text === 'number' && formatearMoneda(text).length > 12) return 'small-text';
    if (typeof text === 'string' && text.includes('TOTAL') && text.length > 10) return 'small-text';
    return '';
  };

  const [editingValues, setEditingValues] = useState({});
  const [defaultValues, setDefaultValues] = useState({});

  const handleEdit = (itemId) => {
    setEditingItem(itemId);
    // Initialize editing values with safe access
    const allItems = [
      ...(currentData.activos?.corrientes || []),
      ...(currentData.activos?.noCorrientes || []),
      ...(currentData.pasivos?.corrientes || []),
      ...(currentData.pasivos?.noCorrientes || [])
    ];
    const item = allItems.find(i => i.id === itemId);

    if (item) {
      const newValues = {
        concepto: item.concepto || '',
        valor: item.valor?.toString() || '0'
      };
      setEditingValues(prev => ({
        ...prev,
        [itemId]: newValues
      }));
      setDefaultValues(prev => ({
        ...prev,
        [itemId]: newValues
      }));
    }
  };

  const handleSave = async (seccion, tipo, itemId) => {
    const values = editingValues[itemId];
    if (values) {
      await autoSaveItem(seccion, tipo, itemId, 'concepto', values.concepto);
      await autoSaveItem(seccion, tipo, itemId, 'valor', parseFloat(values.valor) || 0);
    }
    setEditingItem(null);
    setEditingValues(prev => {
      const newV = { ...prev };
      delete newV[itemId];
      return newV;
    });
  };

  // Auto-save function
  const autoSaveItem = async (seccion, tipo, itemId, campo, valor) => {
    actualizarItem(seccion, tipo, itemId, campo, valor);
    setHasUnsavedChanges(true);

    // Auto-save to backend immediately
    try {
      await saveBalanceData();
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('❌ Error guardando cambios:', error);
      // Show user-friendly error message
      toast.error('Error guardando cambios. Intente nuevamente.');
    }
  };

  const handleCancel = () => {
    setEditingItem(null);
    setEditingValues({});
    setDefaultValues({});
  };

  const renderTableRow = (item, seccion, tipo) => {
    const isEditing = editingItem === item.id;
    const currentValues = editingValues[item.id] || defaultValues[item.id] || { concepto: item.concepto || '', valor: item.valor?.toString() || '0' };

    return (
      <tr key={item.id} className="has-background-white-bis" style={{ height: '22px' }}>
        <td className={`desc-cell ${getTextClass(currentValues.concepto)}`} style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
          {isEditing ? (
            <input
              className={`input ${getTextClass(currentValues.concepto)}`}
              value={currentValues.concepto ?? ''}
              onChange={(e) => {
                const newValue = e.target.value;
                setEditingValues(prev => ({
                  ...prev,
                  [item.id]: { ...prev[item.id], concepto: newValue }
                }));
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSave(seccion, tipo, item.id);
                }
              }}
              style={{ width: '100%' }}
            />
          ) : (
            item.concepto
          )}
        </td>
        <td className={`value-cell has-text-right has-text-weight-semibold ${getTextClass(parseFloat(currentValues.valor) || 0)}`} style={{ color: '#1e3a8a' }}>
          {isEditing ? (
            <input
              className={`input has-text-right ${getTextClass(parseFloat(currentValues.valor) || 0)}`}
              type="number"
              step="0.01"
              value={currentValues.valor ?? '0'}
              onChange={(e) => {
                const newValue = e.target.value;
                setEditingValues(prev => ({
                  ...prev,
                  [item.id]: { ...prev[item.id], valor: newValue }
                }));
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSave(seccion, tipo, item.id);
                }
              }}
              style={{ width: '100%' }}
            />
          ) : (
            formatearMoneda(item.valor)
          )}
        </td>
        <td className="actions-cell" style={{ minWidth: '70px' }}>
          <div className="buttons are-small" style={{ margin: '0', gap: '2px' }}>
            {isEditing ? (
              <>
                <button className="button is-success is-small" onClick={() => handleSave(seccion, tipo, item.id)}>
                  💾
                </button>
                <button className="button is-light is-small" onClick={handleCancel}>
                  ❌
                </button>
              </>
            ) : (
              <>
                <button className="button is-warning is-small" onClick={() => handleEdit(item.id)}>
                  ✏️
                </button>
                <button
                  className="button is-danger is-small"
                  onClick={() => eliminarItem(seccion, tipo, item.id)}
                >
                  🗑️
                </button>
              </>
            )}
          </div>
        </td>
      </tr>
    );
  };

  // Funciones CRUD
  const agregarItem = (seccion, tipo) => {
    // Calcular maxId de todos los items
    const allItems = [
      ...currentData.activos.corrientes,
      ...currentData.activos.noCorrientes,
      ...currentData.pasivos.corrientes,
      ...currentData.pasivos.noCorrientes
    ];
    const maxId = allItems.length > 0 ? Math.max(...allItems.map(item => item.id)) : 0;

    const newId = maxId + 1;
    // Set the tipo field based on seccion and tipo
    const tipoField = seccion === 'activos' ?
      (tipo === 'corrientes' ? 'activo_corriente' : 'activo_no_corriente') :
      (tipo === 'corrientes' ? 'pasivo_corriente' : 'pasivo_no_corriente');

    const newItem = {
      id: newId,
      concepto: 'Nuevo Concepto',
      valor: 0,
      formula: 'manual',
      tipo: tipoField, // Set the tipo field for proper categorization
      patrimonioId: null // New items don't have backend ID yet
    };

    setBalanceData(prev => {
      const newData = {
        ...prev,
        [seccion]: {
          ...prev[seccion],
          [tipo]: [...prev[seccion][tipo], newItem]
        }
      };

      // Auto-save after state update
      setTimeout(async () => {
        try {
          const success = await saveBalanceData(newData);  // Pass the updated data directly
          setHasUnsavedChanges(false);
          if (success) {
            toast.success('Nuevo item agregado exitosamente');
          } else {
            toast.error('Error agregando nuevo item. Intente nuevamente.');
          }
        } catch (error) {
          console.error('❌ Error guardando nuevo item:', error);
          toast.error('Error agregando nuevo item. Intente nuevamente.');
        }
      }, 0);

      return newData;
    });

    setHasUnsavedChanges(true);
    setEditingItem(newId);
  };

  const eliminarItem = async (seccion, tipo, id) => {
    // Find the item to check if it has a patrimonioId
    const itemToDelete = [
      ...balanceData.activos.corrientes,
      ...balanceData.activos.noCorrientes,
      ...balanceData.pasivos.corrientes,
      ...balanceData.pasivos.noCorrientes
    ].find(item => item.id === id);

    // If it has a patrimonioId, delete from backend
    if (itemToDelete?.patrimonioId) {
      try {
        const response = await apiRequest(`/patrimonios/${itemToDelete.patrimonioId}`, {
          method: 'DELETE'
        });
        if (!response.ok) {
          console.error('Error deleting patrimonio from backend:', response.status);
          toast.error('Error eliminando del backend, pero se eliminará localmente');
        }
      } catch (error) {
        console.error('Error deleting patrimonio from backend:', error);
        toast.error('Error eliminando del backend, pero se eliminará localmente');
      }
    }

    // Remove from local state
    setBalanceData(prev => ({
      ...prev,
      [seccion]: {
        ...prev[seccion],
        [tipo]: prev[seccion][tipo].filter(item => item.id !== id)
      }
    }));
    setHasUnsavedChanges(true);
  };

  const actualizarItem = (seccion, tipo, id, campo, valor) => {
    setBalanceData(prev => ({
      ...prev,
      [seccion]: {
        ...prev[seccion],
        [tipo]: prev[seccion][tipo].map(item =>
          item.id === id ? { ...item, [campo]: valor } : item
        )
      }
    }));
  };


  // Show loading state
  if (loading) {
    return (
      <div className="container" style={{ padding: '2rem', textAlign: 'center' }}>
        <div className="notification is-info">
          <p>Cargando datos del patrimonio...</p>
        </div>
      </div>
    );
  }

  // Don't render if data structure is invalid
  if (!currentData || !currentData.activos || !currentData.pasivos ||
      !currentData.activos.corrientes || !currentData.activos.noCorrientes ||
      !currentData.pasivos.corrientes || !currentData.pasivos.noCorrientes) {
    console.error('Invalid data structure detected:', currentData);
    return (
      <div className="container" style={{ padding: '2rem', textAlign: 'center' }}>
        <div className="notification is-danger">
          <p>Error en la estructura de datos.</p>
          <button
            className="button is-small is-warning"
            onClick={() => loadBalanceData()}
          >
            🔄 Recargar Datos
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Estilos personalizados */}
      <style>
        {`
          .small-text {
            font-size: 0.8rem !important;
          }
          @media (max-width: 768px) {
            .small-text {
              font-size: 0.7rem !important;
            }
          }
          .title.small-text, .subtitle.small-text {
            font-size: 0.85rem !important;
          }
          .level {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
          }
          .level-left, .level-right {
            flex: 1 1 auto;
            min-width: 0;
          }
          .level-item {
            word-break: break-word;
            overflow-wrap: break-word;
          }
          .header-container {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 1rem;
            background: linear-gradient(135deg, #1e3a8a 0%, #b8860b 100%);
            color: white;
            border-radius: 5px;
          }
          .header-field {
            margin-right: 1rem;
          }
          .input {
            max-width: none !important;
          }
          /* Ajustes de legibilidad y espacios en tablas */
          .table.is-fullwidth.is-hoverable td,
          .table.is-fullwidth.is-hoverable th {
            padding: 2px 6px !important;
            line-height: 1.1 !important;
            vertical-align: middle !important;
          }
          .patrimonio-container .table tbody tr {
            height: 22px !important;
          }
          /* Aplicar estilos a todas las tablas de activos y pasivos */
          .patrimonio-container .table td:first-child {
            padding: 1px 4px !important;
            font-size: 0.75rem !important;
            white-space: nowrap;
            max-width: 150px;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .patrimonio-container .table td:nth-child(2) {
            padding: 1px 4px !important;
            font-size: 1.1rem !important;
            white-space: nowrap;
            font-weight: bold;
          }
          .actions-cell {
            padding: 1px 3px !important;
            white-space: nowrap;
          }
          .patrimonio-container .buttons.are-small .button,
          .patrimonio-container .button.is-small {
            height: 22px !important;
            min-height: 22px !important;
            padding: 1px 6px !important;
            font-size: 0.78rem !important;
          }
          .input,
          .input.is-small {
            font-size: 0.85rem !important;
            height: 28px !important;
          }
          .title.is-4, .subtitle.is-5 {
            font-size: 1.35rem !important;
          }
          .pn-center-box {
            width: min(780px, 92%);
            margin: 0 auto 1rem auto;
            background-color: #b8860b;
            color: white;
            border-radius: 6px;
            padding: 8px 12px;
          }
          .pn-center-box .label {
            font-weight: 700;
            letter-spacing: .5px;
          }
          .patrimonio-container .box {
            padding: 8px 10px !important;
          }
          .has-background-warning { padding: 0.6rem !important; }
          .has-background-info { padding: 0.6rem !important; }
          .level { margin-bottom: 6px !important; }
        `}
      </style>

      <div className="patrimonio-container">
        {/* Navegación de pestañas */}
        <div className="tabs is-centered is-boxed mb-5">
          <ul>
            <li>
              <a href="/dashboard/contabilidad/ingresos">
                <span className="icon is-small"><i className="fas fa-dollar-sign" aria-hidden="true"></i></span>
                <span>Cuentas por Cobrar</span>
              </a>
            </li>
            <li>
              <a href="/dashboard/contabilidad/egresos">
                <span className="icon is-small"><i className="fas fa-minus" aria-hidden="true"></i></span>
                <span>Egresos</span>
              </a>
            </li>
            <li className="is-active">
              <a>
                <span className="icon is-small"><i className="fas fa-chart-pie" aria-hidden="true"></i></span>
                <span>Patrimonio</span>
              </a>
            </li>
            <li>
              <a href="/dashboard/contabilidad/calculo-costos">
                <span className="icon is-small"><i className="fas fa-calculator" aria-hidden="true"></i></span>
                <span>Cálculo de Costos</span>
              </a>
            </li>
            <li>
              <a href="/dashboard/contabilidad/resumen">
                <span className="icon is-small"><i className="fas fa-chart-bar" aria-hidden="true"></i></span>
                <span>Resumen</span>
              </a>
            </li>
          </ul>
        </div>

        <h1 className="patrimonio-title">Estado de Situación Patrimonial</h1>

        {/* Botones de acción */}
        <div style={{ marginBottom: '1rem', textAlign: 'right', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button
            className="button is-small is-info"
            onClick={loadBalanceData}
            disabled={loading}
          >
            🔄 Recargar Datos
          </button>
        </div>

        {/* Header */}
        <div className="box header-container">
          <div>
            <h1 className={`title is-3 has-text-white ${getTextClass('Estado de Situación Patrimonial')}`}>
              Estado de Situación Patrimonial
            </h1>
            <div className="field is-grouped">
              <div className="control header-field">
                <label className="label has-text-white">Empresa:</label>
                {(esConexAdmin || authUsuario?.rol === 'conexadmin') ? (
                  <div className="select is-small">
                    <select
                      value={empresaSeleccionada?.id || ''}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        if (selectedId) {
                          const empresa = empresas?.find(emp => String(emp.id) === String(selectedId));
                          if (empresa) {
                            cambiarEmpresa(empresa);
                          }
                        }
                      }}
                      style={{ width: '200px' }}
                    >
                      <option value="">Seleccionar empresa</option>
                      {empresas?.map(empresa => (
                        <option key={empresa.id} value={empresa.id}>
                          {empresa.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <input
                    className={`input is-small ${getTextClass(empresaSeleccionada?.nombre || 'Empresa')}`}
                    value={empresaSeleccionada?.nombre || 'Empresa'}
                    readOnly
                    style={{ width: '200px', backgroundColor: '#f5f5f5' }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="columns" style={{ margin: '0 auto' }}>
          {/* ACTIVOS */}
          <div className="column is-6">
            <div className="box">
              <div className="has-background-warning" style={{ backgroundColor: '#b8860b', padding: '0.9rem', marginBottom: '0.9rem' }}>
                <h2 className={`title is-3 has-text-white has-text-centered ${getTextClass('ACTIVO')}`}>
                  ACTIVO
                </h2>
              </div>

              {/* Activo Corriente */}
              <div className="mb-4">
                <div className="level">
                  <div className="level-left">
                    <h3 className={`subtitle is-5 has-text-weight-bold ${getTextClass('Activo Corriente')}`} style={{ color: '#1e3a8a' }}>
                      Activo Corriente
                    </h3>
                  </div>
                  <div className="level-right">
                    <button
                      className="button is-small is-success"
                      onClick={() => agregarItem('activos', 'corrientes')}
                    >
                      ➕
                    </button>
                  </div>
                </div>

                <div className="table-container" style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
                   <table className="table is-fullwidth is-hoverable">
                     <tbody>
                       {(currentData.activos?.corrientes || []).map(item => (
                         renderTableRow(item, "activos", "corrientes")
                       ))}
                       <tr style={{ backgroundColor: '#b8860b', color: 'white', height: '36px' }}>
                         <td className={`has-text-weight-bold ${getTextClass('Total Activo Corriente')}`}>
                           Total Activo Corriente
                         </td>
                         <td className={`has-text-right has-text-weight-bold ${getTextClass(calcularTotalActivoCorriente())}`}>
                           {formatearMoneda(calcularTotalActivoCorriente())}
                         </td>
                         <td></td>
                       </tr>
                     </tbody>
                   </table>
                 </div>
              </div>

              {/* Activo No Corriente */}
              <div className="mb-4">
                <div className="level">
                  <div className="level-left">
                    <h3 className={`subtitle is-5 has-text-weight-bold ${getTextClass('Activo No Corriente')}`} style={{ color: '#1e3a8a' }}>
                      Activo No Corriente
                    </h3>
                  </div>
                  <div className="level-right">
                    <button
                      className="button is-small is-success"
                      onClick={() => agregarItem('activos', 'noCorrientes')}
                    >
                      ➕
                    </button>
                  </div>
                </div>

                <div className="table-container" style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
                  <table className="table is-fullwidth is-hoverable" style={{ marginBottom: '1rem' }}>
                    <tbody>
                      {(currentData.activos?.noCorrientes || []).map(item => (
                        renderTableRow(item, "activos", "noCorrientes")
                      ))}
                      <tr style={{ backgroundColor: '#b8860b', color: 'white', height: '32px' }}>
                        <td className={`has-text-weight-bold ${getTextClass('Total Activo No Corriente')}`}>
                          Total Activo No Corriente
                        </td>
                        <td className={`has-text-right has-text-weight-bold ${getTextClass(calcularTotalActivoNoCorriente())}`}>
                          {formatearMoneda(calcularTotalActivoNoCorriente())}
                        </td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Total Activo */}
              <div className="box" style={{ backgroundColor: '#1e3a8a', color: 'white', display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                <div className="level">
                  <div className="level-left">
                    <h3 className={`title is-3 has-text-white ${getTextClass('TOTAL ACTIVO')}`} style={{ wordBreak: 'break-word', marginRight: '1rem' }}>
                      TOTAL ACTIVO
                    </h3>
                  </div>
                  <div className="level-right">
                    <h3 className={`title is-3 has-text-white ${getTextClass(calcularTotalActivo())}`}>
                      {formatearMoneda(calcularTotalActivo())}
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* PASIVOS */}
          <div className="column is-6">
            <div className="box">
              <div className="has-background-info" style={{ backgroundColor: '#1e3a8a', padding: '0.9rem', marginBottom: '0.9rem' }}>
                <h2 className={`title is-3 has-text-white has-text-centered ${getTextClass('PASIVO')}`}>
                  PASIVO
                </h2>
              </div>

              {/* Pasivo Corriente */}
              <div className="mb-4">
                <div className="level">
                  <div className="level-left">
                    <h3 className={`subtitle is-5 has-text-weight-bold ${getTextClass('Pasivo Corriente')}`} style={{ color: '#1e3a8a' }}>
                      Pasivo Corriente
                    </h3>
                  </div>
                  <div className="level-right">
                    <button
                      className="button is-small is-success"
                      onClick={() => agregarItem('pasivos', 'corrientes')}
                    >
                      ➕
                    </button>
                  </div>
                </div>

                <div className="table-container" style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
                  <table className="table is-fullwidth is-hoverable">
                    <tbody>
                      {(currentData.pasivos?.corrientes || []).map(item => (
                        renderTableRow(item, "pasivos", "corrientes")
                      ))}
                      <tr style={{ backgroundColor: '#1e3a8a', color: 'white', height: '24px' }}>
                        <td className={`has-text-weight-bold ${getTextClass('Total Pasivo Corriente')}`}>
                          Total Pasivo Corriente
                        </td>
                        <td className={`has-text-right has-text-weight-bold ${getTextClass(calcularTotalPasivoCorriente())}`}>
                          {formatearMoneda(calcularTotalPasivoCorriente())}
                        </td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pasivo No Corriente */}
              <div className="mb-4">
                <div className="level">
                  <div className="level-left">
                    <h3 className={`subtitle is-5 has-text-weight-bold ${getTextClass('Pasivo No Corriente')}`} style={{ color: '#1e3a8a' }}>
                      Pasivo No Corriente
                    </h3>
                  </div>
                  <div className="level-right">
                    <button
                      className="button is-small is-success"
                      onClick={() => agregarItem('pasivos', 'noCorrientes')}
                    >
                      ➕
                    </button>
                  </div>
                </div>

                <div className="table-container" style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
                  <table className="table is-fullwidth is-hoverable" style={{ marginBottom: '1rem' }}>
                    <tbody>
                      {(currentData.pasivos?.noCorrientes || []).map(item => (
                        renderTableRow(item, "pasivos", "noCorrientes")
                      ))}
                      <tr style={{ backgroundColor: '#1e3a8a', color: 'white', height: '24px' }}>
                        <td className={`has-text-weight-bold ${getTextClass('Total Pasivo No Corriente')}`}>
                          Total Pasivo No Corriente
                        </td>
                        <td className={`has-text-right has-text-weight-bold ${getTextClass(calcularTotalPasivoNoCorriente())}`}>
                          {formatearMoneda(calcularTotalPasivoNoCorriente())}
                        </td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Total Pasivo */}
              <div className="box" style={{ backgroundColor: '#1e3a8a', color: 'white', display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                <div className="level">
                  <div className="level-left">
                    <h3 className={`title is-3 has-text-white ${getTextClass('TOTAL PASIVO')}`} style={{ wordBreak: 'break-word', marginRight: '1rem' }}>
                      TOTAL PASIVO
                    </h3>
                  </div>
                  <div className="level-right">
                    <h3 className={`title is-3 has-text-white ${getTextClass(calcularTotalPasivo())}`}>
                      {formatearMoneda(calcularTotalPasivo())}
                    </h3>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Patrimonio Neto centrado inferior */}
        <div className="pn-center-box box">
          <div className="level" style={{ alignItems: 'center' }}>
            <div className="level-left">
              <span className="label">PATRIMONIO NETO</span>
            </div>
            <div className="level-right">
              <span className="has-text-weight-bold" style={{ fontSize: '1.35rem' }}>
                {formatearMoneda(calcularPatrimonioNeto())}
              </span>
            </div>
          </div>
        </div>

        {/* Control de equivalencia contable */}
        <div className="box">
          <div className="level">
            <div className="level-item has-text-centered">
              <div>
                <p className={`heading ${getTextClass('Control de equivalencia contable')}`}>
                  Control de equivalencia contable
                </p>
                <p className={`title ${getTextClass('ACTIVO = PASIVO + PN')}`} style={{ color: calcularTotalActivo() === (calcularTotalPasivo() + calcularPatrimonioNeto()) ? '#48c774' : '#f14668' }}>
                  ACTIVO = PASIVO + PN
                </p>
                <p className={`subtitle ${getTextClass(calcularTotalActivo() === (calcularTotalPasivo() + calcularPatrimonioNeto()) ? '✓ Balanceado' : '⚠ Desbalanceado')}`}>
                  {calcularTotalActivo() === (calcularTotalPasivo() + calcularPatrimonioNeto()) ?
                    '✓ Balanceado' : '⚠ Desbalanceado'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Patrimonio;
